import Cookies from 'js-cookie';
import { Controller } from 'stimulus';
import events from 'lib/events';
import {
  getFormattedPageValue, triggerCustomEvent, addLoadingOverlay, featureEnabled, splitTestEnabled,
} from 'lib/helpers';
import SearchStateManager from 'lib/search-state';

export default class SearchController extends Controller {
  connect() {
    this.searchState = new SearchStateManager();

    this.searchFormInput = document.querySelector('[data-target="search-form.input"]');

    if (this.searchFormInput) {
      this.contentType = this.searchFormInput.getAttribute('data-content-type');

      // hacky support for png in pagination
      if (window.location.href.indexOf('/free-png/') > -1) this.contentType = 'png';
    }

    if (featureEnabled('search-grid-reveal')) {
      addLoadingOverlay(document.querySelector('.search-results'), true);
    }

    window.addEventListener('PostSearchAjax', this.postAjaxEvent);
    window.addEventListener('SearchPaginationChange', this.searchPaginationEvent);
    window.addEventListener('Searched', this.performSearch);
    window.addEventListener('click', this.trackSearchClick);
    window.addEventListener('click', this.trackSponsoredSearchClick);
    window.addEventListener(events.search.apply_filters, this.applySearchFilters);
    window.addEventListener(events.search.update_content_type, this.resetContentType);
  }

  disconnect() {
    window.removeEventListener('PostSearchAjax', this.postAjaxEvent);
    window.removeEventListener('SearchPaginationChange', this.searchPaginationEvent);
    window.removeEventListener('Searched', this.performSearch);
    window.removeEventListener('click', this.trackSearchClick);
    window.removeEventListener('click', this.trackSponsoredSearchClick);
    window.removeEventListener(events.search.apply_filters, this.applySearchFilters);
    window.removeEventListener(events.search.update_content_type, this.resetContentType);
  }

  applySearchFilters = (event) => {
    this.resetContentType(event);

    Cookies.set('ezSearchType', 'internal');

    if (splitTestEnabled('vertical-grid-infinite-scroll')) {
      const filterEl = document.querySelector('.search-sidebar__sticky-wrapper__inner');
      if (filterEl) window.EezyStickyScrollPositionHelper.preservePositionOnce(filterEl);
    } else {
      window.EezyScrollPositionHelper.preservePositionOnce();
    }

    const queryString = this.searchState.getFlatQueryString();

    if (this.unverifiedPath || this.changingContentType()) {
      Turbolinks.visit(this.getNewSearchUrl(this.searchRoute, this.searchTerm, queryString));
    } else {
      Turbolinks.visit(`${window.location.pathname}${queryString}`);
    }
  }

  postAjaxEvent = (event) => {
    this.postAjax(event.detail.dataHash);
  }

  searchPaginationEvent = (event) => {
    triggerCustomEvent('PaginationChanged', {
      page: event.detail.page,
      stateKey: 'search-state',
      extraStates: ['filterState'],
      ajaxEventName: 'PostSearchAjax',
      resultsTarget: document.querySelector('.search-results'),
    });
  }

  performSearch = (event) => {
    const { searchPath } = event.detail;
    const searchRoute = event.detail.searchRoute ? event.detail.searchRoute : this.searchRoute;
    const term = this.normalizeTerm(event.detail.term);

    let queryString;

    // If search contains new filter params
    if (event.detail.useSearchState) {
      queryString = this.searchState.getFlatQueryString().substring(1);
    }

    this.resetContentType(event);

    let newSearchUrl;

    if (searchPath) {
      newSearchUrl = searchPath;
    } else {
      newSearchUrl = this.getNewSearchUrl(searchRoute, term, queryString);
    }

    if (event.detail.searchInNewTab === true) {
      window.open(newSearchUrl, '_blank');
    } else {
      Turbolinks.visit(newSearchUrl);
    }
  }

  resetContentType = (event) => {
    this.contentType = event.detail.contentType || this.contentType;

    if (event.detail.contentType) {
      Cookies.set('content_type', event.detail.contentType);
    }

    if (this.changingContentType()) {
      this.searchState.resetStateToDefault();
      if (!splitTestEnabled('remove-mobile-filters-apply')) triggerCustomEvent('search:reset_hidden_filters');
    }
  }

  changingContentType() {
    return this.contentType !== this.searchFormInput.getAttribute('data-content-type');
  }

  toggleSearchFilters() {
    const toggleSearchFiltersEvent = new CustomEvent('search:toggle_search_filters');
    window.dispatchEvent(toggleSearchFiltersEvent);
  }

  postAjax = (dataHash) => {
    this.searchState.updateStateValue('page', dataHash.page);
    const queryParams = this.searchState.getFlatQueryString();

    if (this.searchType) {
      Cookies.set('ezSearchType', this.searchType);
    }

    const newSearchUrl = this.getNewSearchUrl(this.searchRoute, this.searchTerm, queryParams);

    window.location.assign(newSearchUrl);
  };

  getNewSearchUrl(searchRoute, term, queryString) {
    const url = searchRoute;

    const newQueryParams = [];

    if (searchRoute === this.searchRoute) {
      newQueryParams.push(`qterm=${term}`);
      newQueryParams.push(`content_type=${this.contentType}`);
    }

    if (queryString) {
      queryString.replace(/^\?/, '').split('&').forEach((param) => {
        newQueryParams.push(param);
      });
    }

    if (newQueryParams.length > 0) {
      return `/${url}?${newQueryParams.join('&')}`;
    }

    return url;
  }

  footerPagerChange(event) {
    event.preventDefault(); // eslint-disable-line no-restricted-globals

    const curPageValue = document.querySelector(
      '[data-target="ez-num-pager.currentPage"]',
    );
    const nextPageValue = parseInt(getFormattedPageValue(curPageValue), 10) + 1;

    triggerCustomEvent('SearchPaginationChange', { page: nextPageValue });
  }

  trackSearchClick = (event) => {
    const gridItem = event.target.closest('.search-results .ez-search-results .ez-resource-grid__item');
    if (gridItem) {
      triggerCustomEvent(events.search.click, {
        resourceId: gridItem.getAttribute('data-item-id'),
        resourcePosition: gridItem.getAttribute('data-position'),
        resourcePro: gridItem.getAttribute('data-pro'),
        searchTerm: this.searchTerm,
      });
    }
  }

  trackSponsoredSearchClick = (event) => {
    const sponsoredGridItem = event.target.closest('.search-results .ez-sponsored-results .ez-resource-grid__item');

    if (sponsoredGridItem) {
      triggerCustomEvent(events.search.sponsored_click, {
        resourceId: '',
        resourcePosition: '',
        resourcePro: '',
        searchTerm: this.searchTerm,
        sponsored: true,
      });
    }
  }

  normalizeTerm(term) {
    return encodeURIComponent(term.toLowerCase().trim().replace(/ /g, '-'));
  }

  get searchRoute() {
    if (this.searchFormInput) {
      return this.searchFormInput.dataset.searchRoute;
    }

    return 'search';
  }

  get searchTerm() {
    const searchInfo = this.element.querySelector('[data-search-term]');
    if (searchInfo) {
      return searchInfo.getAttribute('data-search-term');
    }

    return '';
  }

  get searchType() {
    const searchInfo = this.element.querySelector('[data-search-type]');
    if (searchInfo) {
      return searchInfo.getAttribute('data-search-type');
    }

    return null;
  }

  get unverifiedPath() {
    return window.location.pathname === `/${this.searchRoute}`;
  }
}
